const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Brand APIs
export const getBrand = async () => {
  const res = await fetch(`${API}/admin/brand`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch brand settings");
  return res.json();
};

export const saveBrand = async (data) => {
  const res = await fetch(`${API}/admin/brand`, {
    method: "PUT",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error("Failed to save brand settings");
  return res.json();
};

export const presignBrandLogo = async (contentType) => {
  const res = await fetch(`${API}/admin/brand/presign`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ contentType })
  });
  if (!res.ok) throw new Error("Failed to get upload URL");
  return res.json();
};

// Moderation APIs
export const getFlags = async (status = "OPEN") => {
  const res = await fetch(`${API}/admin/reviews/flags?status=${status}&limit=50`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch flags");
  return res.json();
};

export const resolveFlag = async (reviewId, action) => {
  const res = await fetch(`${API}/admin/reviews/${reviewId}/resolve`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ action })
  });
  if (!res.ok) throw new Error("Failed to resolve flag");
  return res.json();
};

// Stats APIs
export const getAdminStats = async () => {
  const res = await fetch(`${API}/admin/stats/overview`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch stats");
  return res.json();
};

// User Management APIs
export const getUsers = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.role) params.append('role', filters.role);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  params.append('limit', '50');
  
  const res = await fetch(`${API}/admin/users?${params.toString()}`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
};

export const getUserProfile = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/profile`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch user profile");
  return res.json();
};

export const banUser = async (userId, reason) => {
  const res = await fetch(`${API}/admin/users/${userId}/ban`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ reason })
  });
  if (!res.ok) throw new Error("Failed to ban user");
  return res.json();
};

export const unbanUser = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/unban`, {
    method: "POST",
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to unban user");
  return res.json();
};

export const sendDirectMessage = async (userId, subject, message) => {
  const res = await fetch(`${API}/admin/messages/direct`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ user_id: userId, subject, message })
  });
  if (!res.ok) throw new Error("Failed to send message");
  return res.json();
};

export const sendBroadcast = async (subject, message, targetRole = "ALL") => {
  const res = await fetch(`${API}/admin/messages/broadcast`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ subject, message, target_role: targetRole })
  });
  if (!res.ok) throw new Error("Failed to send broadcast");
  return res.json();
};

// Promote / Demote
export const promoteUser = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/promote`, {
    method: "POST",
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to promote user");
  }
  return res.json();
};

export const demoteUser = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/demote`, {
    method: "POST",
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to demote user");
  }
  return res.json();
};

// Deactivate / Reactivate
export const deactivateUser = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/deactivate`, {
    method: "POST",
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to deactivate user");
  }
  return res.json();
};

export const reactivateUser = async (userId) => {
  const res = await fetch(`${API}/admin/users/${userId}/reactivate`, {
    method: "POST",
    headers: getAuthHeaders()
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to reactivate user");
  }
  return res.json();
};

// Audit Log
export const getAuditLog = async (limit = 50, skip = 0, action = '') => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('skip', skip);
  if (action) params.append('action', action);
  
  const res = await fetch(`${API}/admin/audit-log?${params.toString()}`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch audit log");
  return res.json();
};

// Brand Reset
export const resetBrand = async () => {
  const res = await fetch(`${API}/admin/brand/reset`, {
    method: "PUT",
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to reset brand");
  return res.json();
};

// Moderation Stats
export const getModerationStats = async () => {
  const res = await fetch(`${API}/admin/moderation/stats`, {
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error("Failed to fetch moderation stats");
  return res.json();
};
